import { useCallback, useState } from "react";
import { CounterpartyReviewField } from "@common/types/counterpartyFieldReview";
import { pluralizeWithCount } from "@common/helpers/string";
import {
    addCounterpartyFieldReviews,
    revokeCounterpartyFieldReview,
    updateCounterpartyFieldReviewsInCache,
} from "@api/counterparty.api";
import { Button } from "@design-system/Button/Button";
import { isViewOnlyRefusalError } from "@lib/viewOnlyRefusal";
import buttonCountChipStyles from "@design-system/Button/buttonCountChip.module.scss";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useUser } from "@hooks/useUser";
import { useScope } from "@state/scopeContext.lib";
import { CounterpartyDirectoryViewRow } from "./counterpartyDirectoryViewTypes";
import {
    isCounterpartyFieldReviewedByUser,
    mergeCounterpartyFieldReviews,
    removeCounterpartyFieldReview,
} from "./cells/counterpartyFieldReviewUtils";

const REVIEW_FIELD = CounterpartyReviewField.ELIGIBLE_1099;

interface DirectoryReviewSelectedButtonProps {
    selectedRows: CounterpartyDirectoryViewRow[];
    mode: "review" | "unreview";
    onClearSelection: () => void;
}

export const DirectoryReviewSelectedButton = ({
    selectedRows,
    mode,
    onClearSelection,
}: DirectoryReviewSelectedButtonProps) => {
    const user = useUser();
    const { workspaceId } = useScope();
    const { toast, error: errorToast } = useToaster();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const selectedCount = selectedRows.length;
    const isUnreview = mode === "unreview";

    const handleClick = useCallback(async () => {
        if (!workspaceId || selectedCount === 0) {
            return;
        }

        setIsSubmitting(true);
        let changedCount = 0;
        let hasFailed = false;

        try {
            for (const row of selectedRows) {
                const counterpartyId = row.counterparty?.id;
                if (!counterpartyId) {
                    continue;
                }

                const isReviewed = isCounterpartyFieldReviewedByUser(
                    row.fieldReviews,
                    REVIEW_FIELD,
                    user.id,
                );

                if (isUnreview) {
                    if (!isReviewed) {
                        continue;
                    }
                    await revokeCounterpartyFieldReview(
                        workspaceId,
                        counterpartyId,
                        REVIEW_FIELD,
                    );
                    updateCounterpartyFieldReviewsInCache(
                        workspaceId,
                        counterpartyId,
                        (existing) =>
                            removeCounterpartyFieldReview(
                                existing,
                                REVIEW_FIELD,
                                user.id,
                            ),
                    );
                    changedCount += 1;
                    continue;
                }

                if (isReviewed) {
                    continue;
                }
                const reviews = await addCounterpartyFieldReviews(
                    workspaceId,
                    counterpartyId,
                    [REVIEW_FIELD],
                );
                updateCounterpartyFieldReviewsInCache(
                    workspaceId,
                    counterpartyId,
                    (existing) =>
                        mergeCounterpartyFieldReviews(existing, reviews),
                );
                changedCount += 1;
            }
        } catch (error) {
            if (isViewOnlyRefusalError(error)) {
                return;
            }
            hasFailed = true;
        } finally {
            setIsSubmitting(false);
        }

        if (hasFailed) {
            const completedAction = isUnreview ? "Unreviewed" : "Reviewed";
            const failedAction = isUnreview ? "unreview" : "review";
            const errorMessage =
                changedCount > 0
                    ? `${completedAction} ${pluralizeWithCount(
                          "counterparty",
                          changedCount,
                      )}, but some failed. Please try again.`
                    : `Failed to ${failedAction} counterparties. Please try again.`;
            errorToast(errorMessage);
            return;
        }

        onClearSelection();
        const completedAction = isUnreview ? "Unreviewed" : "Marked";
        const completedSuffix = isUnreview ? "" : " as reviewed";
        const unchangedMessage = isUnreview
            ? "Selected counterparties are not reviewed"
            : "Selected counterparties are already reviewed";
        const successMessage =
            changedCount > 0
                ? `${completedAction} ${pluralizeWithCount(
                      "counterparty",
                      changedCount,
                  )}${completedSuffix}`
                : unchangedMessage;
        toast(successMessage);
    }, [
        workspaceId,
        selectedCount,
        selectedRows,
        isUnreview,
        user.id,
        onClearSelection,
        toast,
        errorToast,
    ]);

    return (
        <Button
            variant="primary"
            size="md"
            onClick={handleClick}
            disabled={isSubmitting || selectedCount === 0}
            rightIcon={
                <span
                    data-slot-box=""
                    className={buttonCountChipStyles.countChip}
                >
                    {selectedCount}
                </span>
            }
        >
            {isUnreview ? "Unreview" : "Confirm"}
        </Button>
    );
};
